// Leader lines: the thin strokes joining a scope's bubble on the map to its
// callout at the screen edge.
//
// Nearly every one of them is a straight line, and the whole design is built
// around keeping it that way. The callouts sit out past the edge of the map and
// each one belongs to a scope on the rim facing it, so the direct line from
// bubble to label usually misses the map entirely. Where it does, that is what
// gets drawn.
//
// Two straight leaders can never cross. The scopes are ordered by bearing down
// the side and the callouts are stacked in that same order, and straight segments
// between two sequences matched in order do not intersect. That is the whole
// crossing-avoidance argument, and it costs nothing.
//
// Only where the direct line would cut across the map does a leader get routed,
// and then by the smallest deformation that clears it: sample the same straight
// line, and push outwards only the samples that are inside the map's outline.
// The result hugs the outline where it has to and is straight everywhere else, so
// each route stays in its own corridor. Sweeping the bearing round from the scope
// to the callout instead — the obvious way to go "around" — swings a leader well
// clear of where its straight line ran, and straight over its neighbours.
//
// The radius is clamped in priority order: out to clear the map, back in to stay
// on screen, and short of the callout column. The later two are allowed to win,
// so a leader grazes the map for a few degrees rather than running off the top of
// the viewport or in under the labels.

import { centreX, centreY, viewRangeMax, viewportRadiusAt } from "./silhouette";

const TWO_PI = Math.PI * 2;

// Gap between the map's outline and the innermost leader.
const CLEARANCE = 8;

// Radial separation between leaders forced onto the outline over the same
// stretch of bearing. Small: it only has to be enough to read as two strokes.
const LANE_GAP = 4;

// The leader eases out to its clearance over this fraction of the sweep instead
// of stepping out to it immediately, so it leaves the bubble smoothly.
const CLEAR_RAMP = 0.15;

// Spacing between samples along a routed leader, in pixels. Fine enough that the
// smoothing below has something to work with, and that nothing on the map can
// fit between two consecutive samples.
const SAMPLE_SPACING = 24;
const MAX_SEGMENTS = 96;

// Half-width, in samples, of the window the valley-filling below works over.
// Three samples either side is about 8° — wide enough to bridge the gap between
// two neighbouring subtrees, narrow enough to leave the map's real shape alone.
const VALLEY_WINDOW = 3;

// Passes of the neighbour-averaging smoother. Each spreads its influence by one
// sample, so this is a distance in samples, not a convergence target.
const SMOOTHING_PASSES = 4;

// Points tested along a candidate straight leader before accepting it. Twenty
// spans a 1000px line at 50px, well under the smallest thing on the map.
const STRAIGHT_TESTS = 20;

// How far a straight leader may still be inside the map on its way out. Roughly a
// scope bubble and its neighbour, so it covers leaving the rim but not cutting
// across anything.
const STRAIGHT_ESCAPE_PX = 60;

const MIN_RADIUS = 4;
const EDGE_MARGIN = 4;

// Coordinates are rounded to this before going into the path string. Beyond
// keeping the string short, it makes an idle frame produce a byte-identical `d`,
// which both renderers early-out on.
const QUANTUM = 2; // reciprocal of the step, i.e. 0.5px

export const SIDE_LEFT = 0;
export const SIDE_RIGHT = 1;

// Route sampling scratch, reused for every leader.
const MAX_SAMPLES = MAX_SEGMENTS + 1;
const sampleBearing = new Float64Array(MAX_SAMPLES);
const sampleRadius = new Float64Array(MAX_SAMPLES);
const sampleDilated = new Float64Array(MAX_SAMPLES);
const sampleX = new Float64Array(MAX_SAMPLES);
const sampleY = new Float64Array(MAX_SAMPLES);

// Running tally of how well the routing is holding to its own contract: after
// every clamp has been applied, is the leader still outside the map's outline?
// The viewport and column clamps are allowed to win, so this can legitimately be
// non-zero when the map overflows the viewport — but on a normally-fitted estate
// it should be zero, and that is the assertion `window.checkLeaders()` makes.
let auditSamples = 0;
let auditViolations = 0;
let auditWorst = 0;
let auditStraight = 0;
let auditRouted = 0;

export function beginAudit() {
  auditSamples = 0;
  auditViolations = 0;
  auditWorst = 0;
  auditStraight = 0;
  auditRouted = 0;
}

export function auditResult() {
  return {
    samples: auditSamples,
    violations: auditViolations,
    worstPx: auditWorst,
    straight: auditStraight,
    routed: auditRouted,
  };
}

export function wrapPi(a) {
  return ((((a + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI) - Math.PI;
}

// Per-side monotone ordering keys.
//
// Both increase strictly as you travel down that edge of the screen: -π/2 at due
// north, 0 level with the map centre, +π/2 at due south. Ordering callouts by
// this rather than by screen y is what makes the assignment crossing-free — two
// scopes at the same y but different radii sit at different bearings, and y
// order gets them the wrong way round.
//
// Both extend continuously past ±π/2 onto the far half of the map, so a scope
// moved across for load balancing still has a well-defined place in the order.
// The branch cut sits at due east for the left key and due west for the right,
// where no callout on that side can be.
export function bearingToKey(theta, side) {
  return side === SIDE_LEFT ? wrapPi(Math.PI - theta) : wrapPi(theta);
}

// Lane index per leader, for the case where two of them are pinned against the
// map's outline over the same stretch of bearing.
//
// `lane[i] > lane[j]` for every overlapping j > i: the leader that appears
// earlier in the column rides outside. That direction is forced. Leader j starts
// its sweep inside leader i's, so it has to begin below it; and leader i ends its
// sweep by climbing out to its callout, which it can only do without crossing j
// if it is already outside. Both conditions give the same answer, and because the
// constraint always points the same way down the column it can never form a
// cycle.
//
// Assigning backwards means every j > i is already known. n is small enough that
// the quadratic scan does not matter.
export function assignLanes(n, keyFrom, keyTo, lanes) {
  for (let i = n - 1; i >= 0; i--) {
    const lo = Math.min(keyFrom[i], keyTo[i]);
    const hi = Math.max(keyFrom[i], keyTo[i]);

    let lane = 0;
    for (let j = i + 1; j < n; j++) {
      if (Math.max(keyFrom[j], keyTo[j]) < lo) continue;
      if (Math.min(keyFrom[j], keyTo[j]) > hi) continue;
      if (lanes[j] >= lane) lane = lanes[j] + 1;
    }

    lanes[i] = lane;
  }
}

// Append one leader to `parts` as its own subpath. Every leader on a side goes
// into a single path element, so the whole side costs two nodes and two attribute
// writes a frame rather than one of each per callout.
export function emitLeader(parts, leader, bounds) {
  const { sourceBearing, sourceRadius, dockX, tipX, posY, lane } = leader;

  const cx = centreX();
  const cy = centreY();

  const sourceX = cx + Math.cos(sourceBearing) * sourceRadius;
  const sourceY = cy + Math.sin(sourceBearing) * sourceRadius;

  const clearance = CLEARANCE + lane * LANE_GAP;

  // The route aims at `dockX`, which is short of the callout's own point whenever
  // a wider callout stands between the two, and a horizontal stub along this
  // leader's own row carries it the rest of the way. `dockX` is worked out per
  // leader from the rows it actually crosses, so the stub is as long as the
  // difference in width to whatever was in the way and no longer — nothing for
  // most leaders, and never the full width of the widest label on the side.
  const stub = dockX !== tipX;

  // Nearly always, a straight line from the bubble to the callout already misses
  // the map — the callout is out past the edge of it and the scope is on the rim
  // facing that way. Draw the straight line when it is clear, and only route
  // around when it is not.
  //
  // Two of these can never cross: the scopes are in bearing order down the side
  // and the callouts are stacked in the same order, and straight segments between
  // two sequences matched in order do not intersect. Curving unconditionally gave
  // up that guarantee for no gain on the great majority of leaders that never
  // needed it.
  if (straightIsClear(sourceX, sourceY, dockX, posY, cx, cy, clearance)) {
    auditStraight++;
    parts.push(
      "M",
      round(sourceX),
      round(sourceY),
      "L",
      round(dockX),
      round(posY)
    );
    if (stub) parts.push("L", round(tipX), round(posY));
    return;
  }

  // The straight line is blocked, so route around — by taking that same straight
  // line and pushing outwards only the parts of it that are inside the map.
  //
  // The alternative, sweeping the bearing from the scope round to the callout,
  // swings the route well away from where the straight line ran and straight
  // across a neighbouring leader on the way. Deforming the straight line keeps
  // every route in its own corridor: it differs from the direct line only where
  // the map is actually in the way, and where the map is in the way for one
  // leader it is in much the same way for its neighbour.
  auditRouted++;

  const toAnchor = dockX - cx;
  const length = Math.hypot(dockX - sourceX, posY - sourceY);
  const steps = Math.min(
    MAX_SEGMENTS,
    Math.max(8, Math.ceil(length / SAMPLE_SPACING))
  );

  let previousBearing = sourceBearing;

  for (let step = 0; step <= steps; step++) {
    const t = step / steps;
    const px = sourceX + (dockX - sourceX) * t;
    const py = posY * t + sourceY * (1 - t);

    const dx = px - cx;
    const dy = py - cy;
    const bearing = Math.atan2(dy, dx);

    let r = Math.sqrt(dx * dx + dy * dy);

    // `viewRangeMax` covers every bucket between consecutive samples, so a gap
    // narrower than the sampling cannot slip between two of them unnoticed.
    const outline = viewRangeMax(previousBearing, bearing);

    // Eased in over the start, because the route necessarily begins on the map's
    // own edge.
    const ramp = t < CLEAR_RAMP ? t / CLEAR_RAMP : 1;
    const floor = outline + clearance * ramp;
    if (r < floor) r = floor;

    sampleBearing[step] = bearing;
    sampleRadius[step] = r;

    previousBearing = bearing;
  }

  // Both ends are fixed points — the scope's bubble and the callout — and must
  // not be moved by any of the above.
  sampleBearing[0] = sourceBearing;
  sampleRadius[0] = sourceRadius;

  closeValleys(steps);
  smoothOutward(steps);

  // The two limits allowed to win over clearing the map, applied after the
  // smoothing so it cannot push the route back through either of them. Grazing
  // the map for a few degrees reads far better than a leader running off the
  // screen or in under the callouts.
  //
  // The second of them is also what lands the route: at the dock's own bearing,
  // the distance to the vertical line through the callout's point is exactly the
  // distance to the point. So the last sample is the callout, reached radially,
  // with no closing segment and no corner.
  for (let k = 1; k <= steps; k++) {
    const bearing = sampleBearing[k];
    let r = sampleRadius[k];

    const room = viewportRadiusAt(bearing, bounds) - EDGE_MARGIN;
    if (r > room) r = room;

    const dirX = Math.cos(bearing);
    if (dirX * toAnchor > 1e-6) {
      const cap = toAnchor / dirX;
      if (r > cap) r = cap;
    }

    if (r < MIN_RADIUS) r = MIN_RADIUS;
    sampleRadius[k] = r;

    sampleX[k] = cx + dirX * r;
    sampleY[k] = cy + Math.sin(bearing) * r;

    if (k / steps >= CLEAR_RAMP) {
      auditSamples++;
      const inside = viewRangeMax(bearing, bearing) - r;
      if (inside > 0) {
        auditViolations++;
        if (inside > auditWorst) auditWorst = inside;
      }
    }
  }

  sampleX[0] = sourceX;
  sampleY[0] = sourceY;

  // Quadratics through the midpoints: each sample becomes a control point and the
  // curve passes through the midpoint between consecutive samples, which is
  // continuous in its tangent everywhere. Cheaper than fitting cubics, and it
  // removes the faceting a polyline shows at this stroke width.
  parts.push("M", round(sampleX[0]), round(sampleY[0]));
  parts.push(
    "L",
    round((sampleX[0] + sampleX[1]) * 0.5),
    round((sampleY[0] + sampleY[1]) * 0.5)
  );
  for (let k = 1; k < steps; k++) {
    parts.push(
      "Q",
      round(sampleX[k]),
      round(sampleY[k]),
      round((sampleX[k] + sampleX[k + 1]) * 0.5),
      round((sampleY[k] + sampleY[k + 1]) * 0.5)
    );
  }
  parts.push("L", round(sampleX[steps]), round(sampleY[steps]));

  if (stub) parts.push("L", round(tipX), round(posY));
}

// Does the straight line from the bubble to the callout get clear of the map and
// stay clear?
//
// Sampled rather than solved: the outline is a piecewise thing with no closed
// form, and a couple of dozen point tests is cheaper than anything analytic would
// be.
//
// The line necessarily *starts* on the map — the bubble is part of it, and
// anything larger sitting at the same bearing puts the outline further out still.
// So the opening stretch is allowed to be inside, bounded by a short absolute
// distance rather than a fraction of the length, and what actually disqualifies a
// line is going back in once it has got out. Testing the whole line against the
// outline instead sent about four leaders in ten off to be routed purely because
// of how they left their own bubble — and routed leaders are where the remaining
// crossings come from.
function straightIsClear(x0, y0, x1, y1, cx, cy, clearance) {
  const length = Math.hypot(x1 - x0, y1 - y0);
  let escaped = false;

  for (let i = 1; i <= STRAIGHT_TESTS; i++) {
    const t = i / STRAIGHT_TESTS;
    const dx = x0 + (x1 - x0) * t - cx;
    const dy = y0 + (y1 - y0) * t - cy;

    const bearing = Math.atan2(dy, dx);
    const r = Math.sqrt(dx * dx + dy * dy);

    if (r >= viewRangeMax(bearing, bearing) + clearance) {
      escaped = true;
      auditSamples++;
      continue;
    }

    // Inside the map. Fine on the way out, never afterwards.
    if (escaped) return false;
    if (t * length > STRAIGHT_ESCAPE_PX) return false;
  }

  return escaped;
}

// Takes the steps out of the radius profile.
//
// Samples are evenly spaced along the straight line, but their bearings are not:
// close to the map the bearing swings quickly and far away it barely moves. The
// outline they are pushed out to is held in fixed angular buckets, so consecutive
// samples land several buckets apart in one place and inside a single bucket in
// another, and the profile comes out as a staircase — which is the wobble.
//
// Raising a sample to the average of its neighbours flattens that. It only ever
// moves a sample outwards, so the clearance survives untouched, and the passes
// are counted rather than run to convergence: each one spreads its influence by a
// single sample, so a handful stays local. Left to converge it would pull the
// whole profile convex, which on a long route means bulging it out to the edge of
// the screen.
function smoothOutward(steps) {
  if (steps < 2) return;

  for (let pass = 0; pass < SMOOTHING_PASSES; pass++) {
    let moved = false;
    for (let k = 1; k < steps; k++) {
      const mean = (sampleRadius[k - 1] + sampleRadius[k + 1]) * 0.5;
      if (mean > sampleRadius[k]) {
        sampleRadius[k] = mean;
        moved = true;
      }
    }
    if (!moved) break;
  }
}

// Morphological closing on the radius profile: dilate, then erode, then keep
// whichever of that and the original is further out.
//
// The map's outline drops most of the way to the root dial in the gaps between
// neighbouring subtrees, and a leader sitting on it would dive into every one of
// those. Closing fills any valley narrower than the window and leaves the broad
// shape alone — which matters, because simply pushing the profile towards convex
// bulges a long sweep all the way out to the edge of the screen.
//
// Taking the max at the end means the result is never inside the original, so
// the clearance the routing depends on survives untouched. Both ends are left as
// they are: one is the scope's bubble and the other the callout.
function closeValleys(steps) {
  if (steps < 2) return;

  for (let k = 0; k <= steps; k++) {
    const lo = Math.max(0, k - VALLEY_WINDOW);
    const hi = Math.min(steps, k + VALLEY_WINDOW);
    let m = sampleRadius[lo];
    for (let j = lo + 1; j <= hi; j++) {
      if (sampleRadius[j] > m) m = sampleRadius[j];
    }
    sampleDilated[k] = m;
  }

  for (let k = 1; k < steps; k++) {
    const lo = Math.max(0, k - VALLEY_WINDOW);
    const hi = Math.min(steps, k + VALLEY_WINDOW);
    let m = sampleDilated[lo];
    for (let j = lo + 1; j <= hi; j++) {
      if (sampleDilated[j] < m) m = sampleDilated[j];
    }
    if (m > sampleRadius[k]) sampleRadius[k] = m;
  }
}

function round(v) {
  return Math.round(v * QUANTUM) / QUANTUM;
}
